import { useCallback } from 'react';
import { AccountStatus } from 'api/account';
import {
  AddMembersRequestPayload,
  ChangePlanEstimateResponse,
} from 'api/billing/types';
import { confirm } from 'components/atoms/modal';
import { useAccount } from 'hooks/use-account';
import { useTranslate } from 'hooks/use-translation';
import {
  MembersEstimateMutationType,
  useAvailableLicensesQuery,
  useSubscriptionQuery,
} from 'queries/billing';
import { getEstimateModalDescription } from './estimate-modal-description';
import styles from './modal.module.css';

interface Props {
  onLicenseCountValidated: (values: {
    payload: AddMembersRequestPayload;
    afterBillingConfirmation: boolean;
  }) => void;
  estimateMutation: MembersEstimateMutationType;
}

interface Result {
  validateLicenseCount: (payload: AddMembersRequestPayload) => void;
  isEstimateLoading: boolean;
}

export const useManageAgentLicenses = ({
  onLicenseCountValidated,
  estimateMutation,
}: Props): Result => {
  const availableLicenses = useAvailableLicensesQuery();
  const { mutateAsync: getEstimate, isLoading: isEstimateLoading } =
    estimateMutation();
  const { data: subscription } = useSubscriptionQuery();
  const { status } = useAccount();
  const translate = useTranslate();
  const showEstimateModal = useCallback(
    ({
      estimate,
      inviteCount,
      payload,
    }: {
      estimate: ChangePlanEstimateResponse;
      inviteCount: number;
      payload: AddMembersRequestPayload;
    }) => {
      const title = translate('billing.invite.members.estimate.modal.title', {
        additionalCount: inviteCount - availableLicenses,
      });
      const okText = translate('billing.invite.members.confirm.text');

      const message = getEstimateModalDescription({
        estimate,
        subscription,
        availableLicenses,
        inviteCount,
      });

      confirm({
        title,
        content: message,
        className: styles.modal_method,
        closable: true,
        icon: '',
        width: 520,
        okText,
        onOk: () =>
          onLicenseCountValidated({ payload, afterBillingConfirmation: true }),
      });
    },
    [availableLicenses, onLicenseCountValidated, subscription, translate],
  );

  const validateLicenseCount = useCallback(
    async (payload: AddMembersRequestPayload) => {
      /**
       * When account is on trial, there is no restriction on inviting members
       * since it wouldnt involve payment.
       */
      if (status === AccountStatus.Trial) {
        onLicenseCountValidated({ payload, afterBillingConfirmation: false });

        return;
      }
      const inviteCount = payload.length;

      if (inviteCount > availableLicenses) {
        const estimateResponse = await getEstimate(payload);
        const { amountDue, creditsApplied } = estimateResponse;

        if (amountDue !== 0 || creditsApplied !== 0) {
          showEstimateModal({
            estimate: estimateResponse,
            inviteCount,
            payload,
          });
        } else {
          onLicenseCountValidated({ payload, afterBillingConfirmation: false });
        }
      } else {
        onLicenseCountValidated({ payload, afterBillingConfirmation: false });
      }
    },
    [
      availableLicenses,
      getEstimate,
      onLicenseCountValidated,
      showEstimateModal,
      status,
    ],
  );

  return {
    validateLicenseCount,
    isEstimateLoading,
  };
};
